import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded'
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded'
import DomainAddRoundedIcon from '@mui/icons-material/DomainAddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import GroupRoundedIcon from '@mui/icons-material/GroupRounded'
import { makeAccessApi } from 'services/accessService'
import SearchableSelect from 'components/SearchableSelect'
import AccessAddModal from './AccessAddModal'
import DomainRuleMembers from './DomainRuleMembers'
import { SkeletonRows } from 'components/Skeleton'
import { ROLE_OPTIONS, roleLabel } from 'utils/roleOptions'
import { hideEmail } from 'utils/user'
import { formatDate } from 'utils/date'
import { LAST_ACTIVE_HELP, lastActiveText, lastActiveTitle } from 'features/accessControl/lastActive'
import { ink, line, radius, surface, font, transition, bp as bpToken } from 'features/dashboard/tokens'
import { dedupNearest, nodeAccessCounts, treeAccessSummary } from 'features/accessControl/treeCounts'

const nodeKey = (node) => `${node.type}:${node.id}`
const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ') || (u?.email ? u.email.split('@')[0] : 'Unknown')
const initial = (u) => (fullName(u).charAt(0) || '?').toUpperCase()
const byName = (a) => (a ? [a.firstName, a.lastName].filter(Boolean).join(' ').trim() : '')
const addedLine = (m) => {
  if (!m.addedAt) return null
  const who = byName(m.addedBy)
  return who ? `Added ${formatDate(m.addedAt)} by ${who}` : `Added ${formatDate(m.addedAt)}`
}

const NODE_ICON = { portfolio: AccountTreeRoundedIcon, estate: ApartmentRoundedIcon, building: HomeWorkRoundedIcon }
const nodePath = (type, id) => ({ portfolio: `/portfolios/${id}`, estate: `/estates/${id}`, building: `/bp-list/${id}` }[type])
const nodeAccessPath = (type, id) => ({ portfolio: `/portfolios/${id}?tab=access`, estate: `/estates/${id}?tab=access`, building: `/bp-list/${id}/accessControl` }[type])
const TYPE_LABEL = { portfolio: 'Whole portfolio', estate: 'Estate', building: 'Building' }
const ADD_LABEL = { portfolio: 'Add to whole portfolio', estate: 'Add to estate', building: 'Add to building' }

const AccessTree = ({ scopeType, scopeId, canManage, isOwner = false, onChanged, reloadKey, onSummary }) => {
  const navigate = useNavigate()
  const [tree, setTree] = useState(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(() => new Set())
  const [primed, setPrimed] = useState(false)
  const [addTarget, setAddTarget] = useState(null)
  const [removeAllTarget, setRemoveAllTarget] = useState(null)
  const [removingAll, setRemovingAll] = useState(false)
  const [menu, setMenu] = useState(null)
  const [domainAddTarget, setDomainAddTarget] = useState(null)
  const [removeGrantTarget, setRemoveGrantTarget] = useState(null)
  const [expandedGrants, setExpandedGrants] = useState(() => new Set())
  const [removingGrant, setRemovingGrant] = useState(false)
  const api = useMemo(() => makeAccessApi(scopeType, scopeId), [scopeType, scopeId])
  const onSummaryRef = useRef(onSummary)
  onSummaryRef.current = onSummary
  useEffect(() => {
    if (tree) onSummaryRef.current?.(treeAccessSummary(tree))
  }, [tree])

  const load = useCallback(() => {
    setLoading(true)
    api.getTree()
      .then((res) => {
        const data = res?.data?.data || null
        setTree(data)
        setPrimed((was) => {
          if (was || !data) return was
          const keys = new Set([nodeKey(data)])
          ;(data.estates || []).forEach((e) => keys.add(nodeKey(e)))
          setExpanded(keys)
          return true
        })
      })
      .catch(() => setTree(null))
      .finally(() => setLoading(false))
  }, [api])

  useEffect(() => { load() }, [load, reloadKey])

  const refresh = useCallback(() => { load(); if (onChanged) onChanged() }, [load, onChanged])

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const nodeApi = useCallback((node) => makeAccessApi(node.type, node.id), [])

  const userGrants = useMemo(() => {
    const map = new Map()
    const visit = (n) => {
      (n.members || []).forEach((m) => {
        const list = map.get(m.user.id) || []
        list.push({ type: n.type, name: n.name, role: m.role, user: m.user })
        map.set(m.user.id, list)
      })
      ;(n.estates || []).forEach(visit)
      ;(n.buildings || []).forEach(visit)
    }
    if (tree) visit(tree)
    return map
  }, [tree])

  const doRemoveAll = async () => {
    if (!removeAllTarget || !api.removeUserEverywhere) return
    setRemovingAll(true)
    try {
      const res = await api.removeUserEverywhere(removeAllTarget.user.id)
      const n = res?.data?.data?.buildingsAffected
      toast.success(typeof n === 'number' ? `Removed from ${n} ${n === 1 ? 'building' : 'buildings'}.` : 'Access removed everywhere.')
      setRemoveAllTarget(null)
      refresh()
    } catch {
      toast.error('Could not remove access.')
    } finally {
      setRemovingAll(false)
    }
  }

  const changeRole = async (node, member, role) => {
    try {
      await nodeApi(node).updateUser(member.rowId, { relationType: role })
      toast.success('Role updated.')
      refresh()
    } catch { toast.error('Could not update role.') }
  }

  const removeMember = async (node, member) => {
    const where = node.type === 'building' ? `from ${node.name}` : `from ${node.name} and everything in it`
    if (!window.confirm(`Remove ${fullName(member.user)} ${where}?`)) return
    try {
      await nodeApi(node).revokeUser(member.rowId)
      toast.success('Access removed.')
      refresh()
    } catch { toast.error('Could not remove access.') }
  }

  const childrenOf = (node) => {
    if (node.type === 'portfolio') return [...(node.estates || []), ...(node.buildings || [])]
    if (node.type === 'estate') return node.buildings || []
    return []
  }

  const toggleGrant = (grant) => {
    setExpandedGrants((prev) => {
      const next = new Set(prev)
      next.has(grant.id) ? next.delete(grant.id) : next.add(grant.id)
      return next
    })
  }

  const removeDomainGrant = async () => {
    if (!removeGrantTarget) return
    const { node, grant } = removeGrantTarget
    setRemovingGrant(true)
    try {
      const res = await nodeApi(node).removeDomainGrant(grant.id)
      const lost = res?.data?.data?.peopleLostAccess
      toast.success(
        lost > 0
          ? `Domain rule for @${grant.domain} removed. ${lost} ${lost === 1 ? 'person' : 'people'} lost access.`
          : `Domain rule for @${grant.domain} removed. No one lost access.`
      )
      setRemoveGrantTarget(null)
      refresh()
    } catch (err) {
      toast.error(err?.response?.data?.message || `Could not remove the domain rule for @${grant.domain}.`)
    } finally {
      setRemovingGrant(false)
    }
  }

  const renderDomainSection = (node) => {
    const grants = node.domainGrants || []
    if (grants.length === 0) return null
    return (
      <DomainSection>
        <DomainHead>
          <DomainTitle>
            <LanguageRoundedIcon style={{ fontSize: 15 }} /> Domain rules
            <DomainHeadCount>{grants.length}</DomainHeadCount>
          </DomainTitle>
          {canManage && (
            <DomainAddBtn type='button' onClick={() => setDomainAddTarget(node)}>
              <DomainAddRoundedIcon style={{ fontSize: 15 }} /> Add domain rule
            </DomainAddBtn>
          )}
        </DomainHead>
        <DomainList>
            {grants.map((g) => {
              const open = expandedGrants.has(g.id)
              const count = g.memberCount || 0
              return (
                <DomainItem key={g.id}>
                  <DomainRow>
                    <DomainMain>
                      <DomainName title={g.domain}><DomainAt>@</DomainAt>{g.domain}</DomainName>
                      {g.alsoCovers?.length > 0 && (
                        <DomainAlsoCovers>
                          Also covers {g.alsoCovers.map((d) => `@${d}`).join(', ')}
                          {g.organisationName ? `, all part of ${g.organisationName}` : ''}
                        </DomainAlsoCovers>
                      )}
                    </DomainMain>
                    <DomainRolePill>{roleLabel(g.relationType)}</DomainRolePill>
                    <DomainCountBtn type='button' onClick={() => toggleGrant(g)} aria-expanded={open}>
                      <GroupRoundedIcon style={{ fontSize: 14 }} />
                      {count} {count === 1 ? 'person' : 'people'} with an account
                      <Chevr $open={open}><ExpandMoreRoundedIcon style={{ fontSize: 16 }} /></Chevr>
                    </DomainCountBtn>
                    {canManage && (
                      <DomainRemoveBtn type='button' aria-label={`Remove domain rule for ${g.domain}`} onClick={() => setRemoveGrantTarget({ node, grant: g })}>
                        <CloseRoundedIcon style={{ fontSize: 15 }} />
                      </DomainRemoveBtn>
                    )}
                  </DomainRow>
                  {open && (
                    <DomainRuleMembers
                      api={nodeApi(node)}
                      grant={g}
                      scopeName={node.name}
                      scopeType={node.type}
                      canManage={canManage}
                      showActivity={isOwner}
                      onChanged={load}
                    />
                  )}
                </DomainItem>
              )
            })}
        </DomainList>
      </DomainSection>
    )
  }

  const renderNode = (node, depth, inherited) => {
    const key = nodeKey(node)
    const kids = childrenOf(node)
    const hasKids = kids.length > 0
    const isOpen = expanded.has(key)
    const counts = nodeAccessCounts(node, inherited)
    const { viaRows, directCount, inheritedCount, insideCount } = counts
    const childInherited = dedupNearest([
      ...inherited,
      ...(node.members || []).map((m) => ({ ...m, sourceName: node.name, sourceType: node.type, sourceId: node.id })),
    ])
    const Icon = NODE_ICON[node.type] || HomeWorkRoundedIcon
    const nobodyHere = directCount === 0 && inheritedCount === 0

    return (
      <NodeBlock key={key}>
        <NodeRow $depth={depth} $root={depth === 0}>
          <Caret
            type='button'
            $open={isOpen}
            onClick={() => toggle(key)}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            <ChevronRightRoundedIcon style={{ fontSize: 18 }} />
          </Caret>
          <NodeIcon $type={node.type}><Icon style={{ fontSize: 17 }} /></NodeIcon>
          {depth === 0 ? (
            <NodeName title={node.name}>{node.name}</NodeName>
          ) : (
            <NodeLink type='button' title={`Open ${node.name}`} onClick={() => navigate(nodePath(node.type, node.id))}>{node.name}</NodeLink>
          )}
          <NodeType>{TYPE_LABEL[node.type]}</NodeType>
          <Counts>
            {directCount > 0 && <CBadge $direct title='Added at this level, cascades to everything below'>{directCount} direct</CBadge>}
            {inheritedCount > 0 && <CBadge title='Comes from a level above'>{inheritedCount} inherited</CBadge>}
            {insideCount > 0 && <CBadge title={`Added directly to a building or estate inside this ${node.type}`}>{insideCount} inside</CBadge>}
            {directCount === 0 && inheritedCount === 0 && insideCount === 0 && <CNone>no one yet</CNone>}
          </Counts>
          <Spacer />
          {canManage && (
            <AddHere type='button' data-tour-id={depth === 0 ? 'accessControl.add-root' : undefined} onClick={() => setAddTarget(node)}>
              <PersonAddRoundedIcon style={{ fontSize: 15 }} /> {ADD_LABEL[node.type]}
            </AddHere>
          )}
        </NodeRow>

        {isOpen && (
          <NodeBody $depth={depth}>
            {node.type !== 'building' && renderDomainSection(node)}
            <Members>
              {(node.members || []).map((m) => (
                <MemberRow key={`d-${m.rowId}`}>
                  <Avatar>{initial(m.user)}</Avatar>
                  <Who>
                    <WhoName>{fullName(m.user)}</WhoName>
                    <WhoMail>{hideEmail(m.user.email)}</WhoMail>
                    {addedLine(m) && <AddedMeta title={addedLine(m)}>{addedLine(m)}</AddedMeta>}
                    {isOwner && <LastSeen $never={!m.user.lastActivityAt} title={`${lastActiveTitle(m.user.lastActivityAt)}. ${LAST_ACTIVE_HELP}`}>{lastActiveText(m.user.lastActivityAt)}</LastSeen>}
                  </Who>
                  {canManage ? (
                    <RoleField>
                      <SearchableSelect
                        ariaLabel={`Role for ${fullName(m.user)}`}
                        value={m.role}
                        options={ROLE_OPTIONS.map(({ value, label }) => ({ value, label }))}
                        onChange={(role) => role && role !== m.role && changeRole(node, m, role)}
                      />
                    </RoleField>
                  ) : (
                    <RolePill>{roleLabel(m.role)}</RolePill>
                  )}
                  <DirectTag>direct</DirectTag>
                  {canManage && (
                    <MenuBtn
                      type='button'
                      aria-label={`More actions for ${fullName(m.user)}`}
                      onClick={(e) => {
                        const r = e.currentTarget.getBoundingClientRect()
                        setMenu((cur) => (cur?.rowId === m.rowId ? null : { rowId: m.rowId, node, member: m, x: r.right, y: r.bottom }))
                      }}
                    >
                      <MoreVertRoundedIcon style={{ fontSize: 18 }} />
                    </MenuBtn>
                  )}
                </MemberRow>
              ))}

              {viaRows.map((m) => (
                <MemberRow key={`i-${m.user.id}`} $muted>
                  <Avatar $muted>{initial(m.user)}</Avatar>
                  <Who>
                    <WhoName>{fullName(m.user)}</WhoName>
                    <WhoMail>{hideEmail(m.user.email)}</WhoMail>
                    {addedLine(m) && <AddedMeta title={addedLine(m)}>{addedLine(m)}</AddedMeta>}
                    {isOwner && <LastSeen $never={!m.user.lastActivityAt} title={`${lastActiveTitle(m.user.lastActivityAt)}. ${LAST_ACTIVE_HELP}`}>{lastActiveText(m.user.lastActivityAt)}</LastSeen>}
                  </Who>
                  <RolePill $muted>{roleLabel(m.role)}</RolePill>
                  {m.sourceType && m.sourceId && !(m.sourceType === tree.type && m.sourceId === tree.id) ? (
                    <ViaLink type='button' title={`Open ${m.sourceName}, where this access was given`} onClick={() => navigate(nodeAccessPath(m.sourceType, m.sourceId))}>
                      <NorthEastRoundedIcon style={{ fontSize: 12 }} /> via {m.sourceName}
                    </ViaLink>
                  ) : (
                    <ViaTag><NorthEastRoundedIcon style={{ fontSize: 12 }} /> via {m.sourceName}</ViaTag>
                  )}
                </MemberRow>
              ))}

              {nobodyHere && (
                insideCount > 0 ? (
                  <Empty>No one is added at this {node.type} level. {insideCount} {insideCount === 1 ? 'person has' : 'people have'} access on buildings inside (expand below to manage them).</Empty>
                ) : (
                  <Empty>
                    No one can access {node.type === 'building' ? 'this building' : `this ${node.type}`} yet.
                    {canManage && <> Use <b>{ADD_LABEL[node.type]}</b>.</>}
                  </Empty>
                )
              )}
            </Members>

            {hasKids && (
              <Kids>{kids.map((child) => renderNode(child, depth + 1, childInherited))}</Kids>
            )}
          </NodeBody>
        )}
      </NodeBlock>
    )
  }

  if (loading && !tree) return <SkeletonRows rows={5} gap={10} widths={['100%', '100%', '100%', '100%', '100%']} />
  if (!tree) return <Empty>Couldn’t load the access tree.</Empty>

  return (
    <>
      <Legend>
        <LegendItem><Dot $direct /> <b>Direct</b>: added here; controls the level below</LegendItem>
        <LegendItem><Dot /> <b>Inherited</b>: comes from a level above (change it there)</LegendItem>
        <LegendItem><Dot /> <b>Inside</b>: added on a building within (expand to manage)</LegendItem>
      </Legend>
      <Tree>{renderNode(tree, 0, [])}</Tree>

      {addTarget && (
        <AccessAddModal
          scopeType={addTarget.type}
          scopeId={addTarget.id}
          api={makeAccessApi(addTarget.type, addTarget.id)}
          scopeName={addTarget.name}
          onClose={() => setAddTarget(null)}
          onDone={refresh}
        />
      )}

      {domainAddTarget && (
        <AccessAddModal
          scopeType={domainAddTarget.type}
          scopeId={domainAddTarget.id}
          api={makeAccessApi(domainAddTarget.type, domainAddTarget.id)}
          scopeName={domainAddTarget.name}
          initialSubject='domain'
          onClose={() => setDomainAddTarget(null)}
          onDone={refresh}
        />
      )}

      {removeGrantTarget && createPortal(
        <Overlay onClick={() => !removingGrant && setRemoveGrantTarget(null)}>
          <Card onClick={(e) => e.stopPropagation()} role='dialog' aria-modal='true'>
            <CardHead>
              <WarnIcon><WarningAmberRoundedIcon style={{ fontSize: 20 }} /></WarnIcon>
              <div>
                <CardTitle>Remove the domain rule for @{removeGrantTarget.grant.domain}?</CardTitle>
                <CardSub>{removeGrantTarget.node.name}</CardSub>
              </div>
            </CardHead>
            <CardBody>
              <BodyLead>People who only had access through this rule will lose it. Anyone added directly keeps their access.</BodyLead>
              {removingGrant && <CardNote>Updating everyone’s access. This can take a little while for large teams.</CardNote>}
            </CardBody>
            <CardActions>
              <CancelBtn type='button' onClick={() => setRemoveGrantTarget(null)} disabled={removingGrant}>Cancel</CancelBtn>
              <ConfirmBtn type='button' onClick={removeDomainGrant} disabled={removingGrant}>{removingGrant ? 'Removing…' : 'Remove rule'}</ConfirmBtn>
            </CardActions>
          </Card>
        </Overlay>,
        document.body
      )}

      {removeAllTarget && createPortal(
        (() => {
          const grants = userGrants.get(removeAllTarget.user.id) || []
          const memberships = grants.filter((g) => g.type !== 'building')
          const directBuildings = grants.filter((g) => g.type === 'building')
          return (
            <Overlay onClick={() => !removingAll && setRemoveAllTarget(null)}>
              <Card onClick={(e) => e.stopPropagation()} role='dialog' aria-modal='true'>
                <CardHead>
                  <WarnIcon><WarningAmberRoundedIcon style={{ fontSize: 20 }} /></WarnIcon>
                  <div>
                    <CardTitle>Remove {fullName(removeAllTarget.user)} from all of {tree.name}?</CardTitle>
                    <CardSub>{hideEmail(removeAllTarget.user.email)}</CardSub>
                  </div>
                </CardHead>
                <CardBody>
                  <BodyLead>This removes every access they were given inside this {tree.type}:</BodyLead>
                  <RemoveList>
                    {memberships.map((g, i) => (
                      <RemoveItem key={`m-${i}`}>
                        <RItemName>{g.name}</RItemName>
                        <RItemMeta>{TYPE_LABEL[g.type]} · {roleLabel(g.role)}, and every building it covers</RItemMeta>
                      </RemoveItem>
                    ))}
                    {directBuildings.map((g, i) => (
                      <RemoveItem key={`b-${i}`}>
                        <RItemName>{g.name}</RItemName>
                        <RItemMeta>Building · {roleLabel(g.role)} · direct access</RItemMeta>
                      </RemoveItem>
                    ))}
                  </RemoveList>
                  <CardNote>
                    {tree.type === 'estate' && 'Access they inherit from the portfolio above isn’t affected. '}
                    This can’t be undone. They’d need to be added again.
                  </CardNote>
                </CardBody>
                <CardActions>
                  <CancelBtn type='button' onClick={() => setRemoveAllTarget(null)} disabled={removingAll}>Cancel</CancelBtn>
                  <ConfirmBtn type='button' onClick={doRemoveAll} disabled={removingAll}>
                    {removingAll ? 'Removing…' : 'Remove everywhere'}
                  </ConfirmBtn>
                </CardActions>
              </Card>
            </Overlay>
          )
        })(),
        document.body
      )}

      {menu && createPortal(
        <>
          <MenuBackdrop onClick={() => setMenu(null)} />
          <MenuDropdown style={{ right: Math.max(8, window.innerWidth - menu.x), top: menu.y + 6 }}>
            <MenuItem type='button' onClick={() => { const mm = menu; setMenu(null); removeMember(mm.node, mm.member) }}>
              {menu.node.type === 'building' ? 'Remove from this building' : 'Remove from here & below'}
            </MenuItem>
            {api.removeUserEverywhere && (userGrants.get(menu.member.user.id)?.length || 0) > 1 && (
              <MenuItem type='button' $danger onClick={() => { const u = menu.member.user; setMenu(null); setRemoveAllTarget({ user: u }) }}>
                Remove from all of {tree?.name}
              </MenuItem>
            )}
          </MenuDropdown>
        </>,
        document.body
      )}
    </>
  )
}

export default AccessTree

const Legend = styled.div`display:flex;flex-wrap:wrap;gap:16px;margin-bottom:12px;font-size:${font.small};color:${ink.mute};`
const LegendItem = styled.span`display:inline-flex;align-items:center;gap:7px; b{color:${ink.soft};font-weight:700;}`
const Dot = styled.span`width:9px;height:9px;border-radius:50%;background:${({ $direct, theme }) => ($direct ? theme.colors.secondary : line.strong)};`

const Tree = styled.div`display:flex;flex-direction:column;`
const NodeBlock = styled.div`min-width:0;`

const NodeRow = styled.div`
  display:flex;align-items:center;gap:10px;padding:10px 12px;min-width:0;
  background:${({ $root }) => ($root ? surface.wash : surface.card)};
  border:1px solid ${line.base};border-radius:${radius.sm};margin-top:8px;
  ${({ $root }) => $root && 'margin-top:0;'}
  @media (max-width:${bpToken.mobile}){flex-wrap:wrap;}
`
const Caret = styled.button`
  flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;
  border:none;background:transparent;color:${ink.mute};cursor:pointer;border-radius:${radius.xs};
  transform:rotate(${({ $open }) => ($open ? '90deg' : '0deg')});transition:${transition};
  &:hover{background:${surface.wash};color:${ink.base};}
`
const NodeIcon = styled.span`
  flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:${radius.sm};
  background:${({ $type, theme }) => ($type === 'building' ? surface.wash : `${theme.colors.secondary}14`)};
  color:${({ $type, theme }) => ($type === 'building' ? ink.mute : theme.colors.secondary)};
`
const NodeName = styled.div`font-size:${font.emphasis};font-weight:700;color:${ink.base};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`
const NodeLink = styled.button`background:none;border:none;padding:0;margin:0;min-width:0;font-size:${font.emphasis};font-weight:700;color:${({ theme }) => theme.colors.secondary};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;cursor:pointer;text-align:left;transition:${transition};&:hover{text-decoration:underline;}`
const NodeType = styled.span`font-size:11px;font-weight:800;letter-spacing:0.06em;text-transform:uppercase;color:${ink.mute};flex-shrink:0;`
const Counts = styled.span`display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap;flex-shrink:0;`
const CBadge = styled.span`
  font-size:11px;font-weight:700;padding:2px 8px;border-radius:${radius.pill};white-space:nowrap;cursor:help;font-variant-numeric:tabular-nums;
  background:${({ $direct, theme }) => ($direct ? `${theme.colors.secondary}1a` : surface.wash)};
  color:${({ $direct, theme }) => ($direct ? theme.colors.secondary : ink.mute)};
  border:1px solid ${({ $direct, theme }) => ($direct ? `${theme.colors.secondary}40` : line.base)};
`
const CNone = styled.span`font-size:11.5px;color:${ink.mute};`
const Spacer = styled.span`flex:1;`
const AddHere = styled.button`
  flex-shrink:0;display:inline-flex;align-items:center;gap:5px;padding:7px 12px;border-radius:${radius.sm};
  border:1px solid ${({ theme }) => theme.colors.secondary};background:${({ theme }) => `${theme.colors.secondary}12`};
  color:${({ theme }) => theme.colors.secondary};font-size:12.5px;font-weight:700;cursor:pointer;white-space:nowrap;transition:${transition};
  &:hover{background:${({ theme }) => theme.colors.secondary};color:#fff;}
  @media (max-width:${bpToken.mobile}){flex-basis:100%;justify-content:center;}
`

const NodeBody = styled.div`
  padding:2px 0 2px 30px;border-left:1px dashed ${line.strong};margin-left:22px;
  @media (max-width:${bpToken.mobile}){padding-left:12px;margin-left:8px;}
`
const Members = styled.div`display:flex;flex-direction:column;gap:6px;padding:8px 0;`
const MemberRow = styled.div`
  display:flex;align-items:center;gap:10px;padding:7px 10px;border-radius:${radius.sm};min-width:0;
  background:${({ $muted }) => ($muted ? 'transparent' : surface.card)};
  border:1px solid ${({ $muted }) => ($muted ? 'transparent' : line.base)};
  opacity:${({ $muted }) => ($muted ? 0.85 : 1)};
  @media (max-width:${bpToken.mobile}){flex-wrap:wrap;}
`
const Avatar = styled.span`
  flex-shrink:0;width:28px;height:28px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;
  font-size:12px;font-weight:700;
  background:${({ $muted, theme }) => ($muted ? surface.wash : `${theme.colors.secondary}1a`)};
  color:${({ $muted, theme }) => ($muted ? ink.mute : theme.colors.secondary)};
`
const Who = styled.div`min-width:0;flex:1;display:flex;flex-direction:column;`
const WhoName = styled.span`font-size:${font.body};font-weight:600;color:${ink.base};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`
const WhoMail = styled.span`font-size:${font.caption};color:${ink.mute};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`
const AddedMeta = styled.span`font-size:${font.caption};color:${ink.mute};opacity:0.85;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:2px;`
const LastSeen = styled.span`font-size:${font.caption};font-weight:600;color:${({ $never }) => ($never ? ink.mute : ink.soft)};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:2px;cursor:help;`
const RoleField = styled.div`flex-shrink:0;width:190px;@media (max-width:${bpToken.mobile}){width:100%;order:3;}`
const RolePill = styled.span`
  flex-shrink:0;font-size:12px;font-weight:700;padding:4px 10px;border-radius:${radius.pill};
  background:${surface.wash};border:1px solid ${line.base};color:${({ $muted }) => ($muted ? ink.mute : ink.soft)};
`
const DirectTag = styled.span`flex-shrink:0;font-size:10px;font-weight:800;letter-spacing:0.05em;text-transform:uppercase;color:${({ theme }) => theme.colors.secondary};`
const ViaTag = styled.span`flex-shrink:0;display:inline-flex;align-items:center;gap:3px;font-size:11.5px;font-weight:600;color:${ink.mute};`
const ViaLink = styled.button`flex-shrink:0;display:inline-flex;align-items:center;gap:3px;background:none;border:none;padding:0;font-size:11.5px;font-weight:700;color:${({ theme }) => theme.colors.secondary};cursor:pointer;transition:${transition};&:hover{text-decoration:underline;}`
const MenuBtn = styled.button`
  flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:${radius.sm};
  border:1px solid ${line.base};background:${surface.card};color:${ink.mute};cursor:pointer;transition:${transition};
  &:hover{background:${surface.wash};color:${ink.base};border-color:${line.strong};}
`
const Kids = styled.div`display:flex;flex-direction:column;padding-bottom:4px;`
const Empty = styled.div`font-size:${font.small};color:${ink.mute};padding:8px 10px; b{color:${ink.soft};}`

const MenuBackdrop = styled.div`position:fixed;inset:0;z-index:100020;background:transparent;`
const MenuDropdown = styled.div`
  position:fixed;z-index:100021;min-width:210px;background:${surface.card};border:1px solid ${line.base};
  border-radius:${radius.sm};box-shadow:0 8px 24px rgba(15,23,42,0.16);padding:5px;display:flex;flex-direction:column;
`
const MenuItem = styled.button`
  text-align:left;padding:9px 12px;border:none;background:transparent;border-radius:${radius.xs};cursor:pointer;
  font-size:13px;font-weight:600;color:${({ $danger }) => ($danger ? '#b00020' : ink.soft)};transition:${transition};
  &:hover{background:${({ $danger }) => ($danger ? '#fdecea' : surface.wash)};color:${({ $danger }) => ($danger ? '#b00020' : ink.base)};}
`
const Overlay = styled.div`
  position:fixed;inset:0;z-index:100010;background:rgba(15,23,42,0.45);
  display:flex;align-items:center;justify-content:center;padding:16px;
`
const Card = styled.div`
  width:100%;max-width:460px;max-height:88vh;overflow:auto;background:${surface.card};
  border:1px solid ${line.base};border-radius:${radius.md};box-shadow:0 12px 40px rgba(15,23,42,0.18);
`
const CardHead = styled.div`display:flex;align-items:flex-start;gap:12px;padding:18px 20px 6px;`
const WarnIcon = styled.span`
  flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:${radius.sm};
  background:#fdecea;color:#b00020;
`
const CardTitle = styled.h3`margin:0;font-size:16px;font-weight:800;color:${ink.base};line-height:1.3;`
const CardSub = styled.div`font-size:${font.caption};color:${ink.mute};margin-top:2px;`
const CardBody = styled.div`padding:8px 20px 4px;`
const BodyLead = styled.div`font-size:${font.body};color:${ink.soft};margin-bottom:10px;`
const RemoveList = styled.div`display:flex;flex-direction:column;gap:6px;`
const RemoveItem = styled.div`padding:9px 12px;border-radius:${radius.sm};background:${surface.wash};border:1px solid ${line.base};`
const RItemName = styled.div`font-size:${font.body};font-weight:700;color:${ink.base};`
const RItemMeta = styled.div`font-size:${font.caption};color:${ink.mute};margin-top:1px;`
const CardNote = styled.div`font-size:${font.caption};color:${ink.mute};margin:14px 0 4px;line-height:1.5;`
const CardActions = styled.div`display:flex;justify-content:flex-end;gap:10px;padding:12px 20px 18px;`
const CancelBtn = styled.button`
  padding:9px 16px;border-radius:${radius.sm};border:1px solid ${line.strong};background:${surface.card};
  color:${ink.soft};font-size:13px;font-weight:700;cursor:pointer;transition:${transition};
  &:hover:not(:disabled){background:${surface.wash};}&:disabled{opacity:0.6;cursor:default;}
`
const ConfirmBtn = styled.button`
  padding:9px 16px;border-radius:${radius.sm};border:1px solid #b00020;background:#b00020;
  color:#fff;font-size:13px;font-weight:700;cursor:pointer;transition:${transition};
  &:hover:not(:disabled){background:#960018;}&:disabled{opacity:0.7;cursor:default;}
`

const DomainSection = styled.div`
  margin:8px 0;padding:12px 14px;border:1px solid ${line.base};border-radius:${radius.sm};background:${surface.wash};
`
const DomainHead = styled.div`display:flex;align-items:center;justify-content:space-between;gap:10px;
  @media (max-width:${bpToken.mobile}){flex-wrap:wrap;}`
const DomainTitle = styled.div`display:inline-flex;align-items:center;gap:6px;font-size:${font.small};font-weight:800;letter-spacing:0.04em;text-transform:uppercase;color:${ink.soft};`
const DomainHeadCount = styled.span`background:${line.base};border-radius:10px;padding:0 7px;font-size:11px;font-weight:700;color:${ink.mute};font-variant-numeric:tabular-nums;`
const DomainAddBtn = styled.button`
  flex-shrink:0;display:inline-flex;align-items:center;gap:5px;padding:6px 11px;border-radius:${radius.sm};
  border:1px solid ${({ theme }) => theme.colors.secondary};background:${({ theme }) => `${theme.colors.secondary}12`};
  color:${({ theme }) => theme.colors.secondary};font-size:12px;font-weight:700;cursor:pointer;white-space:nowrap;transition:${transition};
  &:hover{background:${({ theme }) => theme.colors.secondary};color:#fff;}
  @media (max-width:${bpToken.mobile}){flex-basis:100%;justify-content:center;}
`
const DomainList = styled.div`display:flex;flex-direction:column;gap:6px;margin-top:10px;`
const DomainItem = styled.div`border:1px solid ${line.base};border-radius:${radius.sm};background:${surface.card};overflow:hidden;`
const DomainRow = styled.div`
  display:flex;align-items:center;gap:10px;padding:8px 12px;min-width:0;
  @media (max-width:${bpToken.mobile}){flex-wrap:wrap;}
`
const DomainMain = styled.div`flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;
  @media (max-width:${bpToken.mobile}){flex-basis:100%;}`
const DomainName = styled.div`font-size:${font.body};font-weight:700;color:${ink.base};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`
const DomainAlsoCovers = styled.div`font-size:${font.small};font-weight:500;color:${ink.mute};line-height:1.45;overflow-wrap:anywhere;`
const DomainAt = styled.span`color:${ink.mute};font-weight:700;margin-right:1px;`
const DomainRolePill = styled.span`
  flex-shrink:0;font-size:12px;font-weight:700;padding:4px 10px;border-radius:${radius.pill};
  background:${surface.wash};border:1px solid ${line.base};color:${ink.soft};
`
const DomainCountBtn = styled.button`
  flex-shrink:0;display:inline-flex;align-items:center;gap:5px;padding:5px 9px;border-radius:${radius.pill};
  border:1px solid ${({ theme }) => `${theme.colors.secondary}40`};background:${({ theme }) => `${theme.colors.secondary}12`};
  color:${({ theme }) => theme.colors.secondary};font-size:12px;font-weight:700;cursor:pointer;white-space:nowrap;transition:${transition};font-variant-numeric:tabular-nums;
  &:hover{background:${({ theme }) => `${theme.colors.secondary}22`};}
`
const Chevr = styled.span`display:inline-flex;transform:rotate(${({ $open }) => ($open ? '180deg' : '0deg')});transition:${transition};`
const DomainRemoveBtn = styled.button`
  flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:${radius.sm};
  border:1px solid ${line.base};background:${surface.card};color:${ink.mute};cursor:pointer;transition:${transition};
  &:hover{background:#fdecea;color:#b00020;border-color:#f3c9c4;}
`
